import {useCallback, useEffect} from 'react';
import {useLocation, useSearchParams} from 'react-router';
import {useReportStore} from '../useReportStore';

// Track if we've already initialized from URL to prevent child components from overriding
let hasInitialized = false;
let lastPathname = null;

/**
 * Custom hook to sync left panel state between Zustand and URL params
 * This enables shareable URLs while maintaining state in Zustand
 *
 * Best practices:
 * - Initialize Zustand from URL on mount (single source of truth: URL)
 * - Update URL when Zustand changes (for shareability)
 * - Use replace: true to avoid polluting browser history
 */
export const useLeftPanelSync = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const leftPanelTab = useReportStore(state => state.leftPanelTab);
    const aiView = useReportStore(state => state.aiView);
    const setLeftPanelTab = useReportStore(state => state.setLeftPanelTab);
    const setAiView = useReportStore(state => state.setAiView);

    // Initialize Zustand from URL params ONLY ONCE per page/route
    useEffect(() => {
        // Reset initialization flag if we navigated to a different route
        if (lastPathname !== location.pathname) {
            hasInitialized = false;
            lastPathname = location.pathname;
        }

        // Skip if already initialized by another component on this route
        if (hasInitialized) {
            return;
        }

        hasInitialized = true;

        const urlTab = searchParams.get('leftPanelTab');
        const urlAiView = searchParams.get('aiView');

        if (urlTab && urlTab !== leftPanelTab) {
            setLeftPanelTab(urlTab);
        }
        if (urlAiView && urlAiView !== aiView) {
            setAiView(urlAiView);
        } else if (!urlAiView && aiView) {
            // Clear aiView if not in URL
            setAiView(null);
        }
        // Only run on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Sync URL params when Zustand state changes
    useEffect(() => {
        const urlTab = searchParams.get('leftPanelTab');
        const urlAiView = searchParams.get('aiView');

        // Check if we need to update URL
        const needsUpdate = urlTab !== leftPanelTab ||
            (aiView && urlAiView !== aiView) ||
            (!aiView && urlAiView);

        if (needsUpdate) {
            setSearchParams(prev => {
                const next = new URLSearchParams(prev);

                // Update leftPanelTab
                if (leftPanelTab) {
                    next.set('leftPanelTab', leftPanelTab);
                } else {
                    next.delete('leftPanelTab');
                }

                // Update aiView
                if (aiView) {
                    next.set('aiView', aiView);
                } else {
                    next.delete('aiView');
                }

                return next;
            }, {replace: true});
        }
    }, [leftPanelTab, aiView, searchParams, setSearchParams]);

    // Return wrapped setters that components can use
    const setTabWithSync = useCallback((tab) => {
        setLeftPanelTab(tab);
    }, [setLeftPanelTab]);

    const setAiViewWithSync = useCallback((view) => {
        setAiView(view);
    }, [setAiView]);

    return {
        leftPanelTab,
        aiView,
        setLeftPanelTab: setTabWithSync,
        setAiView: setAiViewWithSync,
    };
};
